import React, { useEffect, useRef } from 'react';

// MIME usado no drag-and-drop dos chips de placeholder (HTML5 nativo). Fonte
// unica — a barra de chips (WaBotConfigPage) importa esta constante para o
// setData e este editor a usa no getData, evitando divergencia silenciosa.
export const DRAG_MIME = 'application/x-bot-placeholder';

// Editor de texto com placeholders renderizados como "chips" atomicos
// (contenteditable=false): aparecem sem as chaves, nao sao editaveis no meio e
// somem inteiros ao apagar. A fonte da verdade continua sendo a string com
// {token} (o que o backend espera) — o contentEditable e apenas a visualizacao.

export interface PlaceholderEditorHandle {
  /** Insere um {token} na posicao atual do cursor (ou no fim). */
  insertToken: (token: string) => void;
}

interface PlaceholderEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  onFocus?: () => void;
  /** Rótulo acessível do campo (a11y — o editor é um role="textbox"). */
  ariaLabel?: string;
}

// {token} -> "token" (rotulo exibido no chip, sem chaves).
function tokenLabel(token: string): string {
  return token.replace(/^\{|\}$/g, '');
}

// Cria o elemento DOM de um chip para um {token}.
function makeChip(token: string): HTMLElement {
  const chip = document.createElement('span');
  chip.dataset.token = token;
  chip.contentEditable = 'false';
  chip.className =
    'mx-0.5 inline-flex items-center rounded-md bg-green-100 px-1.5 py-0.5 align-baseline font-mono text-xs font-medium text-green-700 select-none';
  chip.textContent = tokenLabel(token);
  return chip;
}

// Monta um DocumentFragment com texto + chips a partir da string com {token}.
// (Retorna um fragment em vez de mutar um nó recebido — o caller aplica.)
// Tolera value undefined/null tratando-o como string vazia.
function buildFragment(value: string | undefined | null): DocumentFragment {
  const frag = document.createDocumentFragment();
  // Divide preservando os {tokens} como separadores capturados.
  const partes = (value || '').split(/(\{[a-zA-Z_]+\})/g);
  partes.forEach(parte => {
    if (!parte) return;
    if (/^\{[a-zA-Z_]+\}$/.test(parte)) {
      frag.appendChild(makeChip(parte));
    } else {
      frag.appendChild(document.createTextNode(parte));
    }
  });
  return frag;
}

// Serializa um nó do editor de volta para string com {token}, recursivamente.
// Recursao e necessaria porque o contentEditable do Chrome envolve cada linha
// nova num bloco <div> (e nao em <br>); sem recursar, esses blocos perderiam a
// quebra de linha e os chips aninhados (viravam o rotulo sem as chaves).
function nodeToValue(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent || '';
  }
  if (!(node instanceof HTMLElement)) {
    return '';
  }
  if (node.dataset.token) {
    return node.dataset.token;
  }
  if (node.tagName === 'BR') {
    return '\n';
  }
  // Elemento de bloco (div/p) = nova linha; recursa no conteudo interno.
  const inner = Array.from(node.childNodes).map(nodeToValue).join('');
  const isBloco = node.tagName === 'DIV' || node.tagName === 'P';
  return isBloco ? `\n${inner}` : inner;
}

// Lê o DOM do editor de volta para a string com {token}. O primeiro bloco nao
// deve gerar um \n inicial — por isso a juncao trata o indice 0 a parte.
function domToValue(target: HTMLElement): string {
  const filhos = Array.from(target.childNodes);
  return filhos
    .map((node, i) => {
      const v = nodeToValue(node);
      // Remove o \n inicial que um <div> de bloco geraria no primeiro filho.
      return i === 0 ? v.replace(/^\n/, '') : v;
    })
    .join('');
}

const PlaceholderEditor = React.forwardRef<PlaceholderEditorHandle, PlaceholderEditorProps>(
  ({ value, onChange, placeholder, className, onFocus, ariaLabel }, ref) => {
    const editorRef = useRef<HTMLDivElement>(null);

    // Re-renderiza o DOM apenas quando o `value` externo diverge do que ja esta
    // no editor (ex.: load inicial, restaurar versao, restaurar padrao). Evita
    // reescrever o DOM a cada tecla, o que destruiria a posicao do cursor.
    useEffect(() => {
      const el = editorRef.current;
      if (!el) return;
      if (domToValue(el) !== value) {
        el.replaceChildren(buildFragment(value));
      }
    }, [value]);

    // Insere um nó na posicao atual do cursor (ou no fim, se fora do editor).
    const insertNodeAtCaret = (node: Node) => {
      const el = editorRef.current;
      if (!el) return;
      el.focus();
      const sel = window.getSelection();
      let range: Range;
      if (sel && sel.rangeCount > 0 && el.contains(sel.anchorNode)) {
        range = sel.getRangeAt(0);
        range.deleteContents();
      } else {
        range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
      }
      range.insertNode(node);
      // Move o cursor para logo depois do nó inserido.
      range.setStartAfter(node);
      range.collapse(true);
      sel?.removeAllRanges();
      sel?.addRange(range);
      onChange(domToValue(el));
    };

    React.useImperativeHandle(ref, () => ({
      insertToken: (token: string) => {
        insertNodeAtCaret(makeChip(token));
      },
    }));

    return (
      <div
        ref={editorRef}
        role="textbox"
        tabIndex={0}
        contentEditable
        suppressContentEditableWarning
        aria-multiline="true"
        aria-label={ariaLabel || placeholder || 'Editor de mensagem'}
        data-placeholder={placeholder}
        className={className}
        onInput={() => {
          const el = editorRef.current;
          if (el) onChange(domToValue(el));
        }}
        onFocus={onFocus}
        onDragOver={e => {
          // Permite soltar chips (o tipo e checado no onDrop do container).
          e.preventDefault();
        }}
        onDrop={e => {
          const token = e.dataTransfer.getData(DRAG_MIME);
          if (!token) return;
          e.preventDefault();
          // Posiciona o cursor no ponto do drop antes de inserir.
          const caret = (
            document as Document & {
              caretRangeFromPoint?: (x: number, y: number) => Range | null;
            }
          ).caretRangeFromPoint?.(e.clientX, e.clientY);
          const sel = window.getSelection();
          if (caret && sel) {
            sel.removeAllRanges();
            sel.addRange(caret);
          }
          insertNodeAtCaret(makeChip(token));
        }}
      />
    );
  },
);

PlaceholderEditor.displayName = 'PlaceholderEditor';

export default PlaceholderEditor;
